<template>
  <div class="input-number-wrapper" :class="{ 
    'has-warning': !hasValidation, 
    'has-success': hasValidation, 
    'is-disabled': isDisabled
  }">
    <!-- Label -->
    <label v-if="label && label !== ''" :for="elemId" class="input-label">
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- PrimeVue InputNumber -->
    <InputNumber 
      v-model="internalValue"
      :inputId="elemId"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :invalid="isRequired && !hasValidation"
      :mode="mode"
      :locale="locale"
      :minFractionDigits="minFractionDigits"
      :maxFractionDigits="maxFractionDigits"
      :min="min"
      :max="max"
      :prefix="prefix"
      :suffix="suffix"
      :currency="currency"
      :currencyDisplay="currencyDisplay"
      :useGrouping="useGrouping"
      :showButtons="showButtons"
      :buttonLayout="buttonLayout"
      :step="step"
      :variant="variant"
      :size="size"
      :fluid="fluid"
      :showClear="showClear"
      @update:modelValue="handleUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import InputNumber from 'primevue/inputnumber';

const props = defineProps({
  // Legacy props (backward compatibility)
  elemId: String,
  placeholder: String,
  label: String,
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  modelValue: { type: [String, Number] as import('vue').PropType<string | number | null>, default: null },
  value: [String, Number], // Keep for backward compatibility

  // PrimeVue InputNumber props
  mode: {
    type: String,
    default: 'decimal' // 'decimal', 'currency', or other modes
  },
  locale: {
    type: String,
    default: undefined // Uses user locale by default
  },
  minFractionDigits: {
    type: Number,
    default: undefined
  },
  maxFractionDigits: {
    type: Number,
    default: undefined
  },
  min: {
    type: Number,
    default: undefined
  },
  max: {
    type: Number,
    default: undefined
  },
  prefix: {
    type: String,
    default: undefined
  },
  suffix: {
    type: String,
    default: undefined
  },
  currency: {
    type: String,
    default: undefined // e.g., 'USD', 'EUR', 'BRL'
  },
  currencyDisplay: {
    type: String,
    default: 'symbol' // 'symbol', 'code', 'name'
  },
  useGrouping: {
    type: Boolean,
    default: true
  },
  showButtons: {
    type: Boolean,
    default: false
  },
  buttonLayout: {
    type: String,
    default: 'stacked' // 'stacked', 'horizontal', 'vertical'
  },
  step: {
    type: Number,
    default: 1
  },
  variant: {
    type: String,
    default: 'outlined' // 'outlined' or 'filled'
  },
  size: {
    type: String,
    default: undefined // 'small', 'large'
  },
  fluid: {
    type: Boolean,
    default: false
  },
  showClear: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: number | null];
  'input': [value: number | null];
}>();

// Reactive data - convert to number if string is provided.
// `??` (not `||`): 0 is a legitimate value and must not fall through to the
// legacy `value` prop — with `||` a modelValue of 0 rendered an EMPTY field.
const internalValue = ref<number | null>(parseValue(props.modelValue ?? props.value));

// Helper function to parse value
function parseValue(val: string | number | undefined | null): number | null {
  if (val === '' || val === null || val === undefined) {
    return null;
  }
  const num = Number(val);
  return isNaN(num) ? null : num;
}

// Computed properties
const isRequired = computed(() =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed(() =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed(() =>
  props.validation()
);

// Watchers
watch(() => props.modelValue, (newVal) => {
  internalValue.value = parseValue(newVal);
});

watch(() => props.value, (newVal) => {
  internalValue.value = parseValue(newVal);
});

// Handle update from PrimeVue InputNumber
function handleUpdate(newVal: number | null): void {
  emit('update:modelValue', newVal);
  emit('input', newVal); // Keep for backward compatibility
}
</script>

<style scoped>
.input-number-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.has-warning :deep(.p-inputnumber) {
  border-color: var(--p-yellow-500);
}

.has-success :deep(.p-inputnumber) {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
